import type { FC } from "react"
import React, { useState } from "react"
import type { FlexProps } from "@chakra-ui/react"
import { Text, Flex, Stack, Box } from "@chakra-ui/react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts"

import { shortenIfAddress } from "web3/helpers/address"
import { DATE_FORMATS } from "common/constants/date"
import type { Delegate, GovernanceTopAdvocatesQuery } from "query/graphql"
import { TOP_ADVOCATES_COLOR_LIST } from "governance/constants/colors"
import { useTopAdvocatesChart } from "governance/hooks/useTopAdvocatesChart"
import ActiveDotNew from "common/components/ActiveDotNew"
import { getDateFromTimestamp } from "common/helpers/date"
import { labelNumber, limitNumberDecimals } from "common/helpers/number"
import type { TickProps } from "common/types/chart"
import CardContainer from "common/components/CardContainer"
import {
  getWeightPercentage,
  stringToBigNumber,
} from "common/helpers/bignumber"
import DelegatesChartDot from "common/components/DelegatesChartDot"
import SimpleTextUserIdentity from "common/components/columns/SimpleTextUserIdentity"
import { useIsWhiteLabel } from "whitelabel/hooks/useIsWhiteLabel"

type Props = {
  section: GovernanceTopAdvocatesQuery
}

type TooltipData = {
  active: boolean
  dateLabel: string
  payload: any
}

const GovernanceLineChartNew: FC<Props & FlexProps> = ({ section }) => {
  const { governor, delegates: voters } = section
  const { token } = governor
  const { decimals } = token

  const isWhiteLabel = useIsWhiteLabel()

  const { dataset } = useTopAdvocatesChart({
    voters: voters.nodes as Delegate[],
    decimals,
  })

  const hasTopAdvocatesData = Boolean(dataset.length)

  const [tooltipData, setTooltipData] = useState<TooltipData>({
    active: false,
    dateLabel: "",
    payload: [],
  })

  if (!hasTopAdvocatesData) {
    return (
      <CardContainer title="Top Delegates">
        <Stack direction="column" pt={{ base: 2, md: "auto" }} spacing={4}>
          <Flex align="center" h="16.75rem" justify="center" mt={10} w="full">
            <Text>Nothing to show</Text>
          </Flex>
        </Stack>
      </CardContainer>
    )
  }

  return (
    <CardContainer
      title={
        tooltipData.active &&
        tooltipData.dateLabel &&
        tooltipData.dateLabel.length > 0
          ? tooltipData.dateLabel
          : "Top Delegates"
      }
    >
      <Flex
        align="center"
        h="10rem"
        justify="center"
        mt={0}
        pt={{ base: 2, md: "auto" }}
        w="full"
      >
        <ResponsiveContainer>
          <LineChart
            data={dataset}
            height={200}
            margin={{ top: 0, left: -52, right: 5, bottom: 0 }}
          >
            <CartesianGrid stroke="#EAECF0" vertical={false} />
            <XAxis
              allowDuplicatedCategory={false}
              axisLine={false}
              dataKey="timestamp"
              interval="preserveStartEnd"
              minTickGap={0}
              padding={{ left: 0, right: 0 }}
              stroke="#9FA2B4"
              tick={<XTick dx={0} dy={0} />}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              stroke="#EBF0F7"
              tick={<YTick color="#9FA2B4" dy={3} />}
              tickSize={0}
            />
            <RechartsTooltip
              content={({ active, payload, label }) => {
                if (active && payload) {
                  const date = getDateFromTimestamp(Number(label))
                  const dateLabel = date.format(DATE_FORMATS.monthThYear)

                  setTooltipData({ payload, active, dateLabel })

                  return null
                }

                if (tooltipData.active && !active) {
                  setTooltipData({ ...tooltipData, active: false })
                }

                return null
              }}
            />
            {(voters?.nodes as Delegate[])?.map((node, index) => {
              if (!("account" in node)) return null

              const { account } = node
              const { address } = account

              return (
                <Line
                  key={`${address}_line_chart`}
                  activeDot={
                    <ActiveDotNew color={TOP_ADVOCATES_COLOR_LIST[index]} />
                  }
                  dataKey={address}
                  dot={false}
                  name={shortenIfAddress(address)}
                  stroke={TOP_ADVOCATES_COLOR_LIST[index]}
                  strokeWidth={2}
                  type="monotone"
                />
              )
            })}
          </LineChart>
        </ResponsiveContainer>
      </Flex>
      <Box width="100%">
        {(voters?.nodes as Delegate[])?.map((voter, index) => {
          if (!("account" in voter)) return null

          const { account, votesCount: votes } = voter

          const { token } = section.governor

          const hasTokenSupply = Number(token.supply) > 0
          const hasGovDelegatesVotes =
            Number(section.governor.delegatesVotesCount) > 0

          const rawWeightPercentage = getWeightPercentage(
            stringToBigNumber(votes),
            stringToBigNumber(
              hasTokenSupply
                ? token.supply
                : section.governor.delegatesVotesCount,
            ),
            token.decimals,
          )

          const totalWeightPercentage =
            rawWeightPercentage < 1 || rawWeightPercentage < 10
              ? limitNumberDecimals(rawWeightPercentage, 1)
              : limitNumberDecimals(rawWeightPercentage, 0)

          const votingPowerLabel =
            !hasGovDelegatesVotes && !hasTokenSupply
              ? `< 0.01%`
              : `${totalWeightPercentage}%`

          return (
            <Flex
              key={`governance_top_advocates_table_item_${account.address}`}
              align="center"
              justify="space-between"
              px={0}
              py={1}
            >
              <Flex align="center" justify="space-between" w="full">
                <>
                  <Text color="gray.600" fontSize="14px" fontWeight={500}>
                    <DelegatesChartDot
                      color={TOP_ADVOCATES_COLOR_LIST[index]}
                    />{" "}
                    <SimpleTextUserIdentity
                      key={`governance-top-advocates-identity-${account.address}`}
                      account={account}
                      isWhiteLabel={isWhiteLabel}
                    />
                  </Text>
                  <Text color="gray.600" fontSize="14px">
                    {tooltipData.active && tooltipData.payload
                      ? labelNumber(Number(tooltipData.payload[index].value))
                      : votingPowerLabel}
                  </Text>
                </>
              </Flex>
            </Flex>
          )
        })}
      </Box>
    </CardContainer>
  )
}

export default GovernanceLineChartNew

const YTick: FC<TickProps> = () => null

// removing this entirely will break the dates
const XTick: FC<TickProps> = ({ payload }) => {
  const timestamp = payload?.value

  if (!timestamp) return null

  return null
}
